/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ForwardedRef } from "react";
import { DropDirection } from "react-bootstrap/esm/DropdownContext";
import { Column, Row } from "react-table";

declare module "react-table" {
  // take this file as-is, or comment out the sections that don't apply to your plugin configuration
  /* @ts-ignore */
  export interface TableOptions<D extends Record<string, unknown>>
    extends UseExpandedOptions<D>,
      UseFiltersOptions<D>,
      UseGlobalFiltersOptions<D>,
      UseGroupByOptions<D>,
      UsePaginationOptions<D>,
      UseResizeColumnsOptions<D>,
      UseRowSelectOptions<D>,
      UseRowStateOptions<D>,
      UseSortByOptions<D>,
      // note that having Record here allows you to add anything to the options, this matches the spirit of the
      // underlying js library, but might be cleaner if it's replaced by a more specific type that matches your
      // feature set, this is a safe default.
      Record<string, any> {}
  /* @ts-ignore */
  export interface Hooks<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseExpandedHooks<D>,
      UseGroupByHooks<D>,
      UseRowSelectHooks<D>,
      UseSortByHooks<D> {}
  /* @ts-ignore */
  export interface TableInstance<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseColumnOrderInstanceProps<D>,
      UseExpandedInstanceProps<D>,
      UseFiltersInstanceProps<D>,
      UseGlobalFiltersInstanceProps<D>,
      UseGroupByInstanceProps<D>,
      UsePaginationInstanceProps<D>,
      UseRowSelectInstanceProps<D>,
      UseRowStateInstanceProps<D>,
      UseSortByInstanceProps<D> {}
  /* @ts-ignore */
  export interface TableState<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseColumnOrderState<D>,
      UseExpandedState<D>,
      UseFiltersState<D>,
      UseGlobalFiltersState<D>,
      UseGroupByState<D>,
      UsePaginationState<D>,
      UseResizeColumnsState<D>,
      UseRowSelectState<D>,
      UseRowStateState<D>,
      UseSortByState<D> {}
  /* @ts-ignore */
  export interface ColumnInterface<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseFiltersColumnOptions<D>,
      UseGlobalFiltersColumnOptions<D>,
      UseGroupByColumnOptions<D>,
      UseResizeColumnsColumnOptions<D>,
      UseSortByColumnOptions<D> {}
  /* @ts-ignore */
  export interface ColumnInstance<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseFiltersColumnProps<D>,
      UseGroupByColumnProps<D>,
      UseResizeColumnsColumnProps<D>,
      UseSortByColumnProps<D> {}
  /* @ts-ignore */
  export interface Cell<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseGroupByCellProps<D>,
      UseRowStateCellProps<D> {}
  /* @ts-ignore */
  export interface Row<
    D extends Record<string, unknown> = Record<string, unknown>
  > extends UseExpandedRowProps<D>,
      UseGroupByRowProps<D>,
      UseRowSelectRowProps<D>,
      UseRowStateRowProps<D> {}
}

export type Metadata = {
  sortKey: TableSortKey[];
  sortOrder: string;
};

export type TableColumnResize = {
  id: string;
  value: string;
};

export type TableSortKey = {
  id: string;
  desc: boolean | "";
  index?: number;
  sortIndex?: number;
};

export type OnResizedChange = (update: TableColumnResize[]) => void;
export type OnSortedChange = (sortOrders: TableSortKey[]) => void;
export type OnChangeIndex = (columns: Column[] | unknown[]) => void;
export type OnHideCols = (keys: string[]) => void;
export type OnResetCols = () => void;
type GetAllSelection = (selections: Record<string, boolean>) => void;
type OnDragRow = (from: string, to: string) => void;
type SearchPaginate = (search: SearchPaginateType) => void;

export interface ITableRef {
  clearAllCheckBox: () => void;
  toggleExpandableRowIndex?: (
    // @ts-ignore
    preCheck: (preItems) => void,
    index: number
  ) => void;
}

export interface ITableSelectionRef {
  resetRowActionClick: () => void;
}

export interface IResourceLock {
  locked?: boolean;
  resourceId?: string;
  lockedBy?: string;
  lockedSince?: string;
  type?: string;
  // tooltip shown instead of "Locked by ... since ..." (e.g. "Refresh for update").
  message?: string;
}

export interface IOrderImageStatus {
  orderId?: string;
  imageStatus?: string;
}

export interface RowProps {
  toggleRowSelected?: (rowPath: string, set?: boolean) => void;
  toggleAllRowsSelected?: (set?: boolean) => void;
}
// Section header rendered inside tbody to split rows into groups.
export interface TableSection {
  key: string;
  title: React.ReactNode;
  description?: React.ReactNode; // right aligned hint text.
  showCount?: boolean; // default true.
  hideWhenEmpty?: boolean;
  emptyMessage?: React.ReactNode;
}

export type TableData = {
  /**
   * Skip checkbox column when row is deleting.
   */
  skipCheckboxes?: string[];
  columns?: Column[] | unknown[];
  /**
   * For radio checkbox in study validation table.
   * Props: checkboxes, onHandleSetGroupRadio, disabledRadios
   */
  checkboxes?: Array<{ value: string; name: string; header?: string }>;
  onHandleSetGroupRadio?: (
    group: Array<{ value: string; name: string }>
  ) => void;
  disabledRadios?: boolean;
  onClickHeader?: (key: string) => void;
  /**
   * Customize table columnHidable columns.
   * Props: sortKeys, colsHide, resizable, orderable, hidable, sortable,onResizedChange , onSortedChange, onChangeIndex, onHideCols
   */
  sortKeys?: TableSortKey[];
  colsHide?: Array<string>;
  resizable?: boolean;
  orderable?: boolean;
  hidable?: boolean;
  sortable?: boolean;
  onResizedChange?: OnResizedChange;
  onSortedChange?: OnSortedChange;
  onChangeIndex?: OnChangeIndex;
  onHideCols?: OnHideCols;
  onResetCols?: OnResetCols;
  /**
   * Root props table.
   */
  data?: Record<string, unknown>[] | Array<unknown> | Array<any>;
  deleted?: string[];
  /**
   * Get checkbox select all.
   */
  getAllSelection?: GetAllSelection;
  isDragNDrop?: boolean;
  onDragRow?: OnDragRow;
  /**
   * Render dynamic row actions. like action have length = 3, width will 25 * 3.
   */
  rowActions?: ((row: any) => any)[] | any;
  noDataMessage?: string;
  setSelectedRows?: (item: any) => void;
  getRowId?: (
    row: Record<string, unknown>,
    index: number,
    parent: Row<Record<string, unknown>> | undefined
  ) => string;
  getRowProps?: (row: any, props: any) => void;
  loading?: boolean;
  /**
   * Table type fixed or responsive.
   */
  fixed?: boolean;
  loadingText?: string;
  tableClassName?: string;
  tableRef?: ForwardedRef<unknown>;
  headerStyleNormal?: boolean;
  expandableRow?: boolean;
  showExpandableIcon?: (row: any) => boolean;
  expandableComponent?: (row: any) => React.ReactElement | undefined | null;
  showHeader?: boolean;
  dateTimeFormat?: string;
  // RECORD LOCKS WORKLIST TABLE.
  locks?: IResourceLock[];
  isShowResult?: boolean;
  isMultiSortEvent?: boolean;
  outerWidth?: number;
  // Dynamic direction for TableDropdownFilter. it will show position of overlay trigger.
  drop?: DropDirection;
  sticky?: boolean;
  /**
   * Dark for worklist table.
   */
  isDark?: boolean;
  /**
   * Customize table should have BigText in header column cause it can resizable.
   */
  noWrapHeaderName?: boolean;
  // click to view details
  onClickToView?: (row: any) => void;
  enabledClickToView?: boolean;
  highlightRowIds?: string[]; // for radio select table like search patients, studies orders in document pages.
  tableNoHeader?: boolean; // display header for table.
  preventSelect?: boolean;
  allowSelectWithGroup?: boolean;
  groupLable?: string;
  forceClickToView?: boolean;
  hasRushInData?: boolean;
  renderCRIcon?: (crData: any) => React.ReactNode; // show critical result icon and render popup
  // expanded props
  expandedRows?: Array<boolean>;
  setExpandedRows?: (expanded: Array<boolean>) => void;
  tableContentMaxHeight?: number;
  openLink?: (row: any) => void;
  copyLink?: (row: any) => void;
  tableName?: string;
  // reset sort single key
  resetAndHideSortedColumn?: (key: string) => void;
  // split rows into sections, rows are rendered in the order of sections.
  sections?: TableSection[];
  getRowSection?: (row: any) => string; // return section key of row.original.
};

/**
 * Refer ITableProps above.
 */
export interface ICustomizeSettingTable extends TableData {
  tableSelectionRef?: ForwardedRef<unknown>;
  columnSettings?: DynamicSettingColumnData[];
  deleted?: string[];
  readOnly?: boolean;
  getColumnsSetting?: (items: DynamicSettingColumnData[]) => void;
  setSortKeys?: (sortKeys: TableSortKey[]) => void;
  /** Allow sorting by multiple columns at once. Default true. */
  multiSort?: boolean;
  getSortCombineKeys?: (sortKeys: TableSortKey[]) => void;
  autoSave?: (settings: any[]) => void;
  handleWorklistSelectRow?: (item: any) => void;
  renderCRIcon?: (crData: any) => React.ReactNode; // show critical result icon and render popup
  shouldLoadDefaultBoard?: boolean;
  shouldReRenderSortKeys?: boolean;
}

export interface DynamicSettingColumnData {
  index?: number;
  name?: string;
  sort?: string;
  visible?: boolean;
  width?: number;
  sortIndex?: number;
}

export type TableBuilderData = {
  metadata?: Metadata;
  showPagination?: boolean;
  onSortedChange?: OnSortedChange;
  resizable?: boolean;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  getAllSelection?: GetAllSelection;
  onResizeHandler?: OnResizedChange;
  onDragRow?: OnDragRow;
};

export type FooterData = {
  offset: number;
  numberOfElements: number;
  totalElements?: number;
  first: boolean;
  last: boolean;
  searchPage: SearchPaginate;
  pageNumber: number;
  loading?: boolean;
  className?: string;
  showClickableTotalCount?: boolean;
  showSelectablePageSize?: boolean;
  asyncTotalCount?: number;
  enabledPageSize?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  getTotalCount?: () => void;
  isDark?: boolean;
  countFilter?: any;
};

export interface SearchPaginateType {
  page?: number | 0;
  size?: number | 10;
}

export enum TableDataAction {
  SELECTION = "selection-action",
  ACTION = "action",
  CELL = "cell",
}
